/**
 * Data loading and management utilities
 */

import citiesData from '@/data/cities.json';
import servicesData from '@/data/services.json';
import serviceCityContentData from '@/data/service-city-content.json';
import type { City, Service, PageMetadata, FAQ } from '@/types/data.types';
import { SITE_URL } from '@/lib/env';

/**
 * Get all cities
 */
export function getAllCities(): City[] {
  return citiesData.cities as City[];
}

/**
 * Get city by slug
 */
export function getCityBySlug(slug: string): City | undefined {
  return getAllCities().find(city => city.slug === slug);
}

/**
 * Get all services
 */
export function getAllServices(): Service[] {
  return servicesData.services as Service[];
}

/**
 * Get service by slug
 */
export function getServiceBySlug(slug: string): Service | undefined {
  return getAllServices().find(service => service.slug === slug);
}

/**
 * Get bespoke service/city content if available
 */
export function getServiceCityContent(serviceSlug: string, citySlug: string) {
  const serviceContent = (serviceCityContentData as Record<string, Record<string, unknown>>)[serviceSlug];

  if (!serviceContent) {
    return undefined;
  }

  return serviceContent[citySlug];
}

/**
 * Get all service/city combinations for static generation
 */
export function getAllServiceCityCombinations(): Array<{ service: string; city: string }> {
  const services = getAllServices();
  const cities = getAllCities();
  
  const combinations: Array<{ service: string; city: string }> = [];
  
  services.forEach(service => {
    cities.forEach(city => {
      combinations.push({
        service: service.slug,
        city: city.slug,
      });
    });
  });
  
  return combinations;
}

/**
 * Generate metadata for service/city page
 */
export function generateServiceCityMetadata(
  service: Service,
  city: City,
  baseUrl: string = SITE_URL
): PageMetadata {
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  const resolveWithBase = (value: string) => {
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
    const normalizedPath = value.startsWith('/') ? value : `/${value}`;
    return `${normalizedBase}${normalizedPath}`;
  };
  const title = `${service.name} in ${city.name}, FL | Davis Construction Group`;
  const description = `Professional ${service.name.toLowerCase()} services in ${city.name}, Florida. ${service.shortDescription} Licensed, insured, and trusted by Broward County residents.`;
  const canonical = `${normalizedBase}/services/${service.slug}/${city.slug}`;
  const ogImage = resolveWithBase(service.heroImage);

  return {
    title,
    description,
    canonical,
    ogImage,
    keywords: [
      ...service.keywords,
      city.name,
      `${city.name} ${service.name.toLowerCase()}`,
      `${service.name} ${city.name} FL`,
      'Broward County',
    ],
  };
}

/**
 * Generate localized FAQs for service/city combination
 */
export function generateLocalizedFAQs(service: Service, city: City): typeof service.faqs {
  // Clone the base FAQs and inject city-specific context
  return service.faqs.map(faq => ({
    question: faq.question.replace(/\bBroward County\b/g, city.name),
    answer: faq.answer
      .replace(/\bBroward County\b/g, city.name)
      .replace(/\blocal\b/g, city.name)
      .replace(/\bthe area\b/g, city.name),
  }));
}

/**
 * Get service area cities (for hub pages)
 */
export function getServiceAreaCities(): City[] {
  return getAllCities().sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Get related services (for internal linking)
 */
export function getRelatedServices(currentService: Service, limit: number = 3): Service[] {
  const allServices = getAllServices();
  
  // Prioritize services in the same category
  const sameCategory = allServices.filter(
    s => s.slug !== currentService.slug && s.category === currentService.category
  );
  
  const otherServices = allServices.filter(
    s => s.slug !== currentService.slug && s.category !== currentService.category
  );
  
  return [...sameCategory, ...otherServices].slice(0, limit);
}

/**
 * Get nearby cities (for internal linking)
 */
export function getNearbyCities(currentCity: City, limit: number = 5): City[] {
  const allCities = getAllCities();
  
  // Calculate distance between two coordinates
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };
  
  // Sort cities by distance from current city
  const citiesWithDistance = allCities
    .filter(city => city.slug !== currentCity.slug)
    .map(city => ({
      city,
      distance: calculateDistance(
        currentCity.coordinates.latitude,
        currentCity.coordinates.longitude,
        city.coordinates.latitude,
        city.coordinates.longitude
      ),
    }))
    .sort((a, b) => a.distance - b.distance);
  
  return citiesWithDistance.slice(0, limit).map(item => item.city);
}

/**
 * Get service statistics for display
 */
export function getServiceStats() {
  const services = getAllServices();
  const cities = getAllCities();
  
  return {
    totalServices: services.length,
    totalCities: cities.length,
    totalPages: services.length * cities.length,
    emergencyServices: services.filter(s => s.emergencyAvailable).length,
    avgWarrantyYears: Math.round(
      services.reduce((sum, s) => sum + s.warrantyYears, 0) / services.length
    ),
  };
}

/**
 * Search services and cities
 */
export function searchContent(query: string): {
  services: Service[];
  cities: City[];
} {
  const lowerQuery = query.toLowerCase();
  
  const services = getAllServices().filter(service =>
    service.name.toLowerCase().includes(lowerQuery) ||
    service.keywords.some(kw => kw.toLowerCase().includes(lowerQuery)) ||
    service.shortDescription.toLowerCase().includes(lowerQuery)
  );
  
  const cities = getAllCities().filter(city =>
    city.name.toLowerCase().includes(lowerQuery) ||
    city.slug.toLowerCase().includes(lowerQuery)
  );
  
  return { services, cities };
}

/**
 * Get breadcrumb data for a page
 */
export function getBreadcrumbs(service?: Service, city?: City) {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
  ];
  
  if (service && !city) {
    breadcrumbs.push({ name: service.name, url: `/services/${service.slug}` });
  } else if (service && city) {
    breadcrumbs.push({ name: 'Services', url: '/broward-county-services' });
    breadcrumbs.push({ name: service.name, url: `/services/${service.slug}` });
    breadcrumbs.push({ 
      name: city.name, 
      url: `/services/${service.slug}/${city.slug}` 
    });
  }
  
  return breadcrumbs;
}

/**
 * Get homepage FAQs (uses General Construction FAQs as they're most general)
 */
export function getHomepageFAQs(): FAQ[] {
  const generalConstruction = getServiceBySlug('general-construction');
  return generalConstruction?.faqs || [];
}

